/**
 * Root saga for the New User app
 */
import log from 'loglevel';
import { all, call, put, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';

import { wait, watchStopExplicitWaitingWithDelay } from '../wait/sagas';
import { sendScreenViews, watchOpenView, watchRequestPopLastView } from '../views/sagas';
import { watchServerError } from '../maintenance/sagas';
import { awaitIgnoreUnsupportedBrowser, watchSetBrowserDetails } from '../browser-details/sagas';
import {
    CHECK_PASSWORD_RESET_REQUESTED,
    FINISH_VIEWING_NEW_PASSWORD,
    FINISH_VIEWING_USERNAME,
    INFO_BOX_DISMISSED,
    NEW_PASSWORD_CONFIRMED,
    setNewPassword,
    setPasswordHandoutError,
    setUsername,
    submitPasswordRequest,
    SUBMIT_PASSWORD_REQUEST,
    SUBMIT_REQUEST_PASSWORD_RESET,
} from './actions';
import { throttledTakeEvery } from 'phoenix2/utils';
import { openView, replaceView, setRootView } from 'phoenix2/state/views/actions';
import {
    initConfigParametersFromLocalStorage,
    watchForNewLocalStorageConfigParameters,
} from 'phoenix2/state/config-params/sagas';
import queryString from 'query-string';
import { getBrowserUnsupported } from 'phoenix2/state/browser-details/selectors';
import { addConfigParameters } from 'phoenix2/state/config-params/actions';
import { setPopLastViewURL } from 'phoenix2/state/appstate/actions';
import { sparxWebError } from 'phoenix2/state/errors/sagas';
import { watchAnalyticsEvents } from 'phoenix2/state/analytics/sagas';
import {
    ALREADY_SET_UP,
    CHOOSE_PASSWORD,
    CONFIRM_DETAILS,
    INFO_BOX,
    NEW_USER_VIEW_ID,
    PASSWORD_RESET_REQUESTED,
    PASSWORD_REVEAL_VIEW_ID,
    USERNAME_REVEAL,
} from '../viewstates/view-ids';
import { getFeatureFlag } from '../feature-flags/selectors';
import { FEATURE_FLAG_NEWUSER_STUDENT_CHOOSE_PASSWORD } from '../feature-flags';
import { getNewUserState } from './selectors';
import { makePostRequest } from './common/makePostRequest';
import { handleServerError } from './common/handleServerError';

// V1 URLs
const INVITE_CODE_URL = '/invite';

// V2 URLs
const STUDENT_LOGIN_URL = '/student/'; // Shared.
const STUDENT_USERNAME_URL = '/studentusername';
const STUDENT_SET_PASSWORD_URL = '/studentpassword';
const STUDENT_REQUEST_PASSWORD_RESET_URL = '/request-password-reset';

export function* rootSaga(session, analytics) {
    try {
        yield all([
            initConfigParametersFromLocalStorage(),
            watchForNewLocalStorageConfigParameters(),
            sendScreenViews(),
            watchStopExplicitWaitingWithDelay(),
            watchServerError(),
            watchSetBrowserDetails(),
            watchOpenView(),
            watchSubmitPasswordRequest(),
            watchSubmitRequestPasswordReset(),
            watchFinishViewingNewPassword(),
            watchRequestPopLastView(),
            analytics && watchAnalyticsEvents(analytics),
            initialiseWithQueryParameters(),
            watchFinishViewingUsername(),
            watchNewPasswordConfirmed(),
            watchInfoBoxDismissed(),
            watchCheckPasswordResetGranted(),
        ]);
    } catch (exc) {
        // Take some action with critical error - send the error to
        // the server to be logged, then restart the app
        log.error('Root saga ended due to uncaught exception', exc);

        yield call(sparxWebError);
        // TODO: Handle fatal error! This is tricky as we do not have a
        // a web socket connection to report the error to
    }
}

/**
 * Sets up the new user app
 */
function* initialiseWithQueryParameters() {
    const queryParameters = queryString.parse(location.search);

    // If there is a parameter to disable animations, set in config parameters:
    if (queryParameters.disableAnimations === 'true') {
        yield put(addConfigParameters(fromJS({ disableAnimations: true }), false));
    }

    // If we are stuck at the unsupported browser screen, await dismissal.
    const unsupportedBrowser = yield select(getBrowserUnsupported);
    if (unsupportedBrowser) {
        yield call(awaitIgnoreUnsupportedBrowser);
    }

    // Set the initial root view to be the new user view:
    yield put(
        setRootView({
            type: 'new-user',
            viewId: NEW_USER_VIEW_ID,
        }),
    );

    // If the query param specifying the school is set, append this to the back url
    const school = queryParameters.s;
    const backURL = new URL(settings.sparxwebPath);
    if (school) {
        backURL.searchParams.append('s', school);
    }

    // Set a URL to go to when the user pops the new user view:
    yield put(setPopLastViewURL(backURL.toString()));
}

/**
 * Watches for SUBMIT_PASSWORD_REQUEST actions, and then makes the request
 * to the Teacher Portal REST API
 * @returns {IterableIterator<*>}
 */
function* watchSubmitPasswordRequest() {
    yield throttledTakeEvery(SUBMIT_PASSWORD_REQUEST, doSubmitPasswordRequest, 0);
}

/**
 * Watches for FINISH_VIEWING_USERNAME actions and then goes on to allowing
 * the user to pick their password
 * @returns {Generator<*, void, *>}
 */
function* watchFinishViewingUsername() {
    yield throttledTakeEvery(FINISH_VIEWING_USERNAME, choosePassword, 0);
}

/**
 * Display the choose password screen
 * @returns {Generator<SimpleEffect<"PUT", PutEffectDescriptor<*>>, void, *>}
 */
function* choosePassword() {
    yield put(
        openView('push', {
            viewId: CHOOSE_PASSWORD,
            type: CHOOSE_PASSWORD,
        }),
    );
}

/**
 * Perform a request for a password to the Teacher Portal REST API
 * @param action
 * @returns {IterableIterator<*>}
 */
function* doSubmitPasswordRequest(action) {
    // Not having a TP server URL or school ID are fatal errors for the New User
    // app
    if (!settings.tpServerURL) {
        throw new Error('No TP server URL');
    }
    if (!settings.schoolID) {
        throw new Error('No School ID');
    }

    const choosePasswordMode = yield select(
        getFeatureFlag,
        FEATURE_FLAG_NEWUSER_STUDENT_CHOOSE_PASSWORD,
    );

    if (settings.nuForgotDetailsMode) {
        // If in forgotten details mode display the new screen:
        yield call(passwordResetRequested, action);
    } else if (choosePasswordMode) {
        // If in new choose password mode:
        yield call(doSubmitPasswordRequestV2, action);
    } else {
        // Otherwise we'll just use the old new user flow:
        yield call(doSubmitPasswordRequestV1, action);
    }
}

/**
 * Perform a request to have a new password generated to the Teacher Portal REST API
 * This is only called for newuser-student-choose-password = false
 * @param action
 * @returns {IterableIterator<*>}
 */
function* doSubmitPasswordRequestV1(action) {
    const url = settings.tpServerURL + INVITE_CODE_URL;

    const request = {
        schoolID: settings.schoolID,
        username: action.username,
        firstName: action.firstName,
        lastName: action.lastName,
        dateOfBirth: action.dateOfBirth,
    };

    let response = null;

    try {
        // Make a call to the Teacher Portal REST API, wrapped in an implicit
        // wait to show "Please Wait" to the user in case it takes a long time
        response = yield wait(call(makePasswordHandoutRequestV1, url, request));
    } catch (errorResponse) {
        // If the user's details were invalid, a 401 error is returned, and
        // we show an error message to the user within the form.
        // Otherwise, it is a fatal server error so we restart Sparx Web
        yield call(handleServerError, errorResponse);
        return;
    }

    const { password, firstName, lastName, username } = response.body;

    // Update state with new password and the user's name:
    yield put(setNewPassword(password, firstName, lastName, username));

    // Turn off back button, as the password handout is now complete, so going back is not possible:
    yield put(setPopLastViewURL(null));

    yield put(
        replaceView({
            viewId: PASSWORD_REVEAL_VIEW_ID,
            type: PASSWORD_REVEAL_VIEW_ID,
        }),
    );
}

/**
 * Make a REST call to the Teacher Portal REST service to request a new password
 * @param url the URL of the Teacher Portal REST service
 * @param body an object which must contain a username, date of birth and
 * invite code
 * @returns {Promise}
 */
function makePasswordHandoutRequestV1(url, body) {
    return makePostRequest({ url, body });

    // Uncomment to use mock:
    // return {
    //     body: {
    //         password: 'ABC123',
    //         firstName: 'James',
    //         lastName: 'Steel',
    //         username: 'JSteel',
    //         // error: true,
    //     },
    // };
}

/**
 * Watches for FINISH_VIEWING_NEW_PASSWORD actions, which are dispatched when
 * the user clicks finish on the Password Reveal view. When an action is
 * received, show an alert to get the user to confirm their new password.
 * @returns {IterableIterator<*>}
 */
function* watchFinishViewingNewPassword() {
    yield throttledTakeEvery(FINISH_VIEWING_NEW_PASSWORD, doFinishViewingNewPassword, 0);
}

/**
 * Show an alert to get the user to confirm their new password
 * @returns {IterableIterator<*>}
 */
function* doFinishViewingNewPassword() {
    // Move to the Reveal Password view
    yield put(
        openView('push', {
            viewId: CONFIRM_DETAILS,
            type: CONFIRM_DETAILS,
        }),
    );
}

/**
 * Watch to see when the user has gotten their password & username correct.
 * @returns {Generator<*, void, *>}
 */
function* watchNewPasswordConfirmed() {
    yield throttledTakeEvery(NEW_PASSWORD_CONFIRMED, doNewPasswordConfirmed, 0);
}

/**
 * Called once the user has gotten their username & password correct.
 * @returns {IterableIterator<*>}
 */
function* doNewPasswordConfirmed() {
    const choosePasswordMode = yield select(
        getFeatureFlag,
        FEATURE_FLAG_NEWUSER_STUDENT_CHOOSE_PASSWORD,
    );

    if (choosePasswordMode) {
        yield call(doNewPasswordConfirmedV2);
        return;
    }

    yield call(showInfoPage);
}

/**
 * Watch to see when the user dismissed the info box.
 * @returns {Generator<*, void, *>}
 */
function* watchInfoBoxDismissed() {
    yield throttledTakeEvery(INFO_BOX_DISMISSED, infoBoxDismissed, 0);
}

/**
 * Redirect after details correctly remembered
 */
function infoBoxDismissed() {
    if (settings.sparxwebPath) {
        window.location.replace(settings.sparxwebPath);
    } else {
        window.location.replace(STUDENT_LOGIN_URL);
    }
}

//
// Code for newuser-student-choose-password = true
//

const stripSchoolFromUsername = username => username.split('@')[0];

/**
 * Perform a request to get the user's username to the Teacher Portal REST API - the first step in
 * the process of choosing a password
 * This is only called for newuser-student-choose-password = false
 * @param action
 * @returns {IterableIterator<*>}
 */
function* doSubmitPasswordRequestV2(action) {
    const studentResetPassword = yield call(
        getStudent,
        action.firstName,
        action.lastName,
        action.dateOfBirth,
    );

    if (!studentResetPassword) {
        // Error already handled
        return;
    }

    if (!studentResetPassword.username || studentResetPassword.invite_attempts > 0) {
        yield put(setPasswordHandoutError());
        return;
    }

    const username = stripSchoolFromUsername(studentResetPassword.username);

    yield put(setUsername(action.firstName, action.lastName, action.dateOfBirth, username));

    // Turn off back button, as the password handout is now complete, so going back is not possible:
    yield put(setPopLastViewURL(null));

    if (studentResetPassword.passwordSetAt && !studentResetPassword.passwordResetGrantedAt) {
        yield put(
            replaceView({
                viewId: ALREADY_SET_UP,
                type: ALREADY_SET_UP,
            }),
        );
        return;
    }

    yield put(
        replaceView({
            viewId: USERNAME_REVEAL,
            type: USERNAME_REVEAL,
        }),
    );
}

/**
 * Request the username from the server.
 * @param url
 * @param body
 * @returns {Promise<unknown>}
 */
function makePasswordHandoutRequestV2(url, body) {
    return makePostRequest({ url, body });

    // Uncomment for mock version:
    // return {
    //     body: {
    //         studentResetPassword: {
    //             username: 'jsteel@testschool',
    //             studentID: '123',
    //         },
    //     },
    // };
}

/**
 * Called once the user has got their details correct. Sends request
 * to the server to set the new password then shows the final 'info' screen.
 * @returns {IterableIterator<*>}
 */
function* doNewPasswordConfirmedV2() {
    const newUserState = yield select(getNewUserState);

    const url = settings.tpServerURL + STUDENT_SET_PASSWORD_URL;

    const request = {
        schoolID: settings.schoolID,
        firstName: newUserState.get('firstName'),
        lastName: newUserState.get('lastName'),
        dateOfBirth: newUserState.get('dateOfBirth'),
        password: newUserState.get('password'),
    };

    try {
        // Make a call to the Teacher Portal REST API, wrapped in an implicit
        // wait to show "Please Wait" to the user in case it takes a long time
        yield wait(call(makeSetStudentPasswordRequestV2, url, request));
    } catch (errorResponse) {
        // If the user's details were invalid, a 401 error is returned, and
        // we show an error message to the user within the form.
        // Otherwise, it is a fatal server error so we restart Sparx Web
        yield call(handleServerError, errorResponse);
        return;
    }

    yield call(showInfoPage);
}

/**
 * Make the set-password request to the server
 * @returns {Promise<unknown>}
 */
function makeSetStudentPasswordRequestV2(url, body) {
    return makePostRequest({ url, body });

    // Uncomment to use mock:
    // return {
    //     body: {},
    // };
}

/**
 * Show the final info page after successful remembering of details
 * @returns {IterableIterator<*>}
 */
function* showInfoPage() {
    yield put(
        replaceView({
            viewId: INFO_BOX,
            type: INFO_BOX,
        }),
    );
}

function* watchSubmitRequestPasswordReset() {
    yield throttledTakeEvery(SUBMIT_REQUEST_PASSWORD_RESET, handleSubmitRequestPasswordReset, 0);
}

function* handleSubmitRequestPasswordReset() {
    const newUserState = yield select(getNewUserState);
    const action = submitPasswordRequest(
        newUserState.get('firstName'),
        newUserState.get('lastName'),
        newUserState.get('dateOfBirth'),
    );
    yield call(passwordResetRequested, action);
}

//
// Password reset requested flow, nuForgotDetailsMode = true
//

/**
 * Password reset requested
 */
function* passwordResetRequested(action) {
    const url = settings.tpServerURL + STUDENT_REQUEST_PASSWORD_RESET_URL;

    const request = {
        schoolID: settings.schoolID,
        firstName: action.firstName,
        lastName: action.lastName,
        dateOfBirth: action.dateOfBirth,
    };

    try {
        // Make a call to the Teacher Portal REST API, wrapped in an implicit
        // wait to show "Please Wait" to the user in case it takes a long time
        yield wait(call(makePasswordResetRequest, url, request));
    } catch (errorResponse) {
        // If the user's details were invalid, a 401 error is returned, and
        // we show an error message to the user within the form.
        // Otherwise, it is a fatal server error so we restart Sparx Web
        yield call(handleServerError, errorResponse);
        return;
    }

    const studentResetPassword = yield call(
        getStudent,
        action.firstName,
        action.lastName,
        action.dateOfBirth,
    );

    if (!studentResetPassword) {
        return;
    }

    const username = stripSchoolFromUsername(studentResetPassword.username);

    // Put the details in the state so they can be used later
    yield put(setUsername(action.firstName, action.lastName, action.dateOfBirth, username));

    // Turn off back button, as the password handout is now complete, so going back is not possible:
    yield put(setPopLastViewURL(null));

    // Check to see if a password reset is pending and has already been actioned
    yield call(handleUserAfterResetRequested, studentResetPassword);
}

function makePasswordResetRequest(url, body) {
    return makePostRequest({ url, body });

    // Uncomment to use mock:
    // return {
    //     body: {
    //         studentResetPassword: {
    //             username: 'jsteel@testschool',
    //             studentID: '123',
    //         },
    //     },
    // };
}

/**
 * Watch to see if the user has clicked ont he refresh button as the teacher
 * has reset their password
 * @returns {Generator<*, void, *>}
 */
function* watchCheckPasswordResetGranted() {
    yield throttledTakeEvery(CHECK_PASSWORD_RESET_REQUESTED, checkPasswordResetGranted, 0);
}

/**
 * Make a request to see if the password reset has been granted, if it has show the new-user
 * flow allowing the student to reset their password
 */
function* checkPasswordResetGranted() {
    // Make the request, if password has been reset show the username screen
    // otherwise show the reset requested screen
    const studentResetPassword = yield call(refetchStudentResetPassword);
    yield call(handleUserAfterResetRequested, studentResetPassword);
}

function* handleUserAfterResetRequested(studentResetPassword) {
    const canSetPassword =
        studentResetPassword &&
        (!studentResetPassword.passwordSetAt || studentResetPassword.passwordResetGrantedAt);
    const nextView = canSetPassword ? USERNAME_REVEAL : PASSWORD_RESET_REQUESTED;
    yield put(
        replaceView({
            viewId: nextView,
            type: nextView,
        }),
    );
}

/*
 * Check to see if the student has been granted a password reset
 */
function* refetchStudentResetPassword() {
    const newUserState = yield select(getNewUserState);

    const studentResetPassword = yield call(
        getStudent,
        newUserState.get('firstName'),
        newUserState.get('lastName'),
        newUserState.get('dateOfBirth'),
    );

    return studentResetPassword;
}

/**
 * Get the students details from the server including their username.
 * @param firstName
 * @param lastName
 * @param dateOfBirth
 */
function* getStudent(firstName, lastName, dateOfBirth) {
    const url = settings.tpServerURL + STUDENT_USERNAME_URL;

    const request = {
        schoolID: settings.schoolID,
        firstName,
        lastName,
        dateOfBirth,
    };

    let response;

    try {
        // Make a call to the Teacher Portal REST API, wrapped in an implicit
        // wait to show "Please Wait" to the user in case it takes a long time
        response = yield wait(call(makePasswordHandoutRequestV2, url, request));
    } catch (errorResponse) {
        // If the user's details were invalid, a 401 error is returned, and
        // we show an error message to the user within the form.
        // Otherwise, it is a fatal server error so we restart Sparx Web
        yield handleServerError(errorResponse);
        return response;
    }

    return response.body?.studentResetPassword;
}
